import produce from 'immer';
import { gql } from '@soundxyz/gql-string';
import { useAuth } from '@context/auth/base';
import type {
  CurrentUserPostRepostStatusType,
  CurrentUserPostVoteStatus,
} from '@graphql/reactQuery';
import { setQueryData, useQuery, UserPostStatusDocument } from '@graphql/reactQuery';
import { createBatchingInputStore } from '@store/batchInput';

/*#__PURE__*/ gql(/* GraphQL */ `
  query UserPostStatus($input: QueryUserPostStatusInput!) {
    userPostStatus(input: $input) {
      postId

      voteStatus
      upvoteCount

      repostCount
      repostStatus
    }
  }
`);

const { batchedKeys, useBatchedKey } = createBatchingInputStore({
  chunkLimit: 50,
});

export function usePostStatus({ postId }: { postId: string }) {
  const postIds = useBatchedKey({ key: postId });
  const { isAuthenticatedWithAPI } = useAuth();

  const { data: postStatus } = useQuery(UserPostStatusDocument, {
    variables: !!postIds?.length && {
      input: {
        postIds,
      },
    },
    cacheTime: Infinity,
    select({ data }) {
      for (const statusData of data.userPostStatus) {
        if (statusData.postId === postId) return statusData;
      }
      return;
    },
    enabled: isAuthenticatedWithAPI,
    keepPreviousData: true,
    staleTime: 0,
  });

  return { postStatus, setPostStatus };
}

function setPostStatus({
  postId,
  voteStatus,
  upvoteCount,
  repostCount,
  repostStatus,
}: {
  postId: string;
  voteStatus: CurrentUserPostVoteStatus;
  upvoteCount: number;
  repostCount: number;
  repostStatus: CurrentUserPostRepostStatusType;
}) {
  const postIds = batchedKeys.keys.get(postId);

  if (!postIds) return;

  setQueryData(
    {
      query: UserPostStatusDocument,
      variables: {
        input: {
          postIds,
        },
      },
    },
    previousData => {
      if (!previousData) return previousData;

      return produce(previousData, draft => {
        for (const value of draft.data.userPostStatus) {
          if (value.postId === postId) {
            value.voteStatus = voteStatus;
            value.upvoteCount = upvoteCount;
            value.repostCount = repostCount;
            value.repostStatus = repostStatus;
            return;
          }
        }

        draft.data.userPostStatus.push({
          voteStatus,
          upvoteCount,
          postId,
          repostCount,
          repostStatus,
        });
      });
    },
  );
}
